// src/types/testData.ts

export interface Test {
  id: number;
  title: string;
  description: string;
  is_published: boolean;
  questions: Question[];
  shuffle_ranges?: [number, number][] | null;
  color_mode?: boolean;
  analysis_algorithm?: string | null;
  /** Цветомер: массив из 8 строк-названий меток; null/[] → fallback на hex */
  color_labels?: string[] | null;
  /** Цветомер: матрица 8×8 float, ИЦ[первый][второй] */
  color_pair_index?: number[][] | null;
  /** Индикатор методики (например, «Шкала СД (методика ЧОУ-3)»): по нему
   * AI-пайплайн выбирает промпт обработки первички. */
  methodology?: string | null;
}

export interface Question {
  id: number;
  test_id: number;
  type: 'text' | 'media' | 'instruction';
  content: string;
  info?: string;
  time_limit: number;
  order: number;
  parameters: Record<string, string>;
  media_url?: string;
  is_hidden: boolean;
  allow_custom_answer: boolean;
  /** Кегль шрифта стимула в pt. undefined/null → размер по умолчанию из вёрстки. */
  font_size?: number | null;
  /** Число кликов по ОтО до смены стимула. undefined/null/1 → один клик. */
  clicks_to_advance?: number | null;
  answer_options: AnswerOption[];
}

export interface AnswerOption {
  id?: number;
  question_id?: number;
  content: string;
  coefficient: number;
  parameters: Record<string, string>;
  /** URL изображения/гифки для кнопки-варианта (Медиатека или внешний). undefined → текстовая кнопка. */
  media_url?: string;
}

/** Настройки шкалы (семантический дифференциал), хранятся в Question.parameters. */
export interface ScaleConfig {
  /** 'scale' → показывать ползунок вместо кнопок. */
  answer_form: 'buttons' | 'scale';
  min: number;
  max: number;
  step: number;
  leftLabel: string;
  rightLabel: string;
  midLabel: string;
}

/** Дефолтная шкала: -3…+3, шаг 1, центр 0. */
export const DEFAULT_SCALE_CONFIG: ScaleConfig = {
  answer_form: 'buttons',
  min: -3,
  max: 3,
  step: 1,
  leftLabel: '',
  rightLabel: '',
  midLabel: '0',
};

/** Достаёт настройки шкалы из question.parameters (уже содержатся как строки). */
export function parseScaleConfig(params: Record<string, string> | null | undefined): ScaleConfig {
  const p = params ?? {};
  const num = (key: string, fallback: number): number => {
    const v = parseFloat(p[key]);
    return Number.isFinite(v) ? v : fallback;
  };
  let min = num('scale_min', DEFAULT_SCALE_CONFIG.min);
  let max = num('scale_max', DEFAULT_SCALE_CONFIG.max);
  // Защита от инверсии min/max: меняем местами, чтобы шаг всегда положительный.
  if (min > max) [min, max] = [max, min];
  let step = num('scale_step', DEFAULT_SCALE_CONFIG.step);
  if (step <= 0) step = DEFAULT_SCALE_CONFIG.step;
  return {
    answer_form: p.answer_form === 'scale' ? 'scale' : 'buttons',
    min,
    max,
    step,
    leftLabel: p.scale_left_label ?? '',
    rightLabel: p.scale_right_label ?? '',
    midLabel: p.scale_mid_label || DEFAULT_SCALE_CONFIG.midLabel,
  };
}

export interface ColorAnswerData {
  first: number;       // индекс первого цвета (0-7)
  second: number;      // индекс второго цвета (0-7)
  time_first: number;  // мс от начала стимула до первого нажатия
  time_second: number; // мс от первого до второго нажатия
  time_total: number;  // sum: time_first + time_second
  ic: number;          // индекс цветопары
}

export interface ParticipantInfo {
  name?: string;
  device: string;
  email?: string;
  age?: number;
  gender?: string;
}

export interface Result {
  id: number;
  user_id: number;
  test_id: number;
  score: number;
  time_spent: number;
  completed_at: Date;
  /** id выбранного варианта (один клик) или список id кликов в порядке нажатия (мульти-режим). */
  answers: Record<number, number | number[]>;
  question_times?: Record<number, number>;
  /** Времена каждого клика в мс: {question_id: [t1, t2, ...]} (мульти-режим). */
  click_times?: Record<number, number[]>;
  skip_reasons?: Record<number, 'skip' | 'timeout'>;
  custom_answers?: Record<number, string>;
  color_answers?: Record<number, ColorAnswerData>;
  participant_info?: ParticipantInfo;
  share_code?: string;
  test_title?: string;
  // Метаданные заключения (заполняются API get_my_results).
  conclusion_exists?: boolean;
  conclusion_id?: number | null;
  pdf_ready?: boolean;
  pdf_url?: string | null;
}